import React, {
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

type ComboboxOptionsPortalWrapperProps = {
  anchorRef: RefObject<HTMLElement | null>;
  children: ReactNode;
  itemsCount: number;
};

export const ComboboxOptionsPortalWrapper = ({
  anchorRef,
  children,
  itemsCount,
}: ComboboxOptionsPortalWrapperProps) => {
  const portalRoot = document.getElementById('combobox-portal-root') || document.body;
  const [style, setStyle] = useState<null | React.CSSProperties>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current;

    if (!anchor) {
      return;
    }

    const rect = anchor.getBoundingClientRect();
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const scrollLeft = window.scrollX || document.documentElement.scrollLeft;

    const windowHeight = window.innerHeight;
    const optionsHeight = Math.min(40 * itemsCount + 12, 150);
    const bottomSpace = windowHeight - rect.bottom;
    const openUpward = bottomSpace < optionsHeight;
    const topPosition = openUpward ? rect.top - optionsHeight + scrollTop : rect.bottom + scrollTop;

    setStyle({
      position: 'absolute',
      top: topPosition,
      left: rect.left + scrollLeft,
      width: rect.width,
      zIndex: 9999,
    });
  }, [anchorRef, itemsCount]);

  useEffect(() => {
    const frame = requestAnimationFrame(updatePosition);

    const handleResizeOrScroll = () => updatePosition();
    window.addEventListener('resize', handleResizeOrScroll);
    window.addEventListener('scroll', handleResizeOrScroll, true);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', handleResizeOrScroll);
      window.removeEventListener('scroll', handleResizeOrScroll, true);
    };
  }, [updatePosition]);

  if (!style) return null;

  return createPortal(
    <div ref={wrapperRef} style={style}>
      {children}
    </div>,
    portalRoot,
  );
};
